import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { notification } from "antd";
import { isIOS } from "react-device-detect";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Link,
  Paper,
  Button,
  Stack,
} from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import {
  marketingColors,
  marketingFonts,
  heroBackgroundSx,
  sectionLabelSx,
  sectionTitleSx,
  eventSurfacePaperSx,
  outlineButtonSx,
} from "../../app/components/tickitMarketingTheme";

const AppleWalletPass = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [passUrl, setPassUrl] = useState(null);

  useEffect(() => {
    if (!isIOS) return;

    const generatePass = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE}/wallet/applewallet?ticketId=${id}`
        );
        setPassUrl(response.data.url);
      } catch (error) {
        console.error("Error generating pass:", error);
        notification.error({
          message: "Could not create pass",
          description: "Failed to generate Apple Wallet pass. Try again from your ticket.",
        });
        navigate(-1);
      }
    };

    generatePass();
  }, [id, navigate]);

  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (isIOS) return;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          navigate(-1);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <Box
      sx={{
        ...heroBackgroundSx,
        minHeight: "100vh",
        pt: 10,
        pb: 6,
        px: 2,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Button
          component={RouterLink}
          to="/events"
          variant="text"
          sx={{
            mb: 2,
            px: 0,
            color: marketingColors.p700,
            fontFamily: marketingFonts.display,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "transparent", color: marketingColors.p600 },
          }}
        >
          ← Back to events
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...eventSurfacePaperSx,
            p: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: { xs: 2.5, sm: 3 },
              background: `linear-gradient(135deg, ${marketingColors.p900} 0%, ${marketingColors.p700} 55%, ${marketingColors.p500} 100%)`,
              color: "#fff",
            }}
          >
            <Typography sx={{ ...sectionLabelSx, color: "rgba(255,255,255,0.85)", mb: 1 }}>
              Wallet
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PhoneIphoneIcon sx={{ fontSize: 32, opacity: 0.95 }} />
              <Typography
                sx={{
                  ...sectionTitleSx,
                  color: "#fff",
                  fontSize: { xs: "1.35rem", sm: "1.5rem" },
                  textShadow: "0 1px 2px rgba(30, 5, 80, 0.35)",
                }}
              >
                {isIOS ? "Add to Apple Wallet" : "Apple Wallet on iPhone"}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
            {isIOS && (
              <>
                <Typography
                  sx={{
                    fontFamily: marketingFonts.body,
                    fontSize: "0.95rem",
                    color: marketingColors.gray600,
                    lineHeight: 1.65,
                    mb: 2,
                  }}
                >
                  We are preparing your pass. Tap the badge below when it appears.
                </Typography>
                {!passUrl && (
                  <CircularProgress sx={{ color: marketingColors.p700, my: 2 }} />
                )}
                {passUrl && (
                  <Link href={passUrl} sx={{ display: "inline-block", mt: 1 }}>
                    <img
                      src="https://firebasestorage.googleapis.com/v0/b/apps-static-storage.firebasestorage.app/o/Add_to_Apple_Wallet.png?alt=media&token=43d0c70e-77c6-4462-b768-76e4be6ca082"
                      alt="Add to Apple Wallet"
                      style={{ maxWidth: "160px", height: "auto" }}
                    />
                  </Link>
                )}
              </>
            )}
            {!isIOS && (
              <>
                <Typography
                  sx={{
                    fontFamily: marketingFonts.body,
                    fontSize: "1rem",
                    color: marketingColors.gray600,
                    lineHeight: 1.7,
                    mb: 2,
                  }}
                >
                  Passes can only be added on iPhone. You will be redirected back in{" "}
                  <Box component="span" sx={{ fontWeight: 800, color: marketingColors.p700 }}>
                    {Math.max(0, countdown)}
                  </Box>{" "}
                  seconds.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/events"
                  variant="outlined"
                  sx={{ ...outlineButtonSx, mt: 1 }}
                >
                  Go to events
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AppleWalletPass;
